import {
  faChevronDown,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo, useState } from 'react';

import {
  FEATURE_SURFACE_LABELS,
  getFeatureLabel,
  useFeaturesCatalog,
} from './feature-catalog';
import {
  buildFeatureTree,
  countChecked,
  FEATURE_FILTER,
  filterFeatureNodes,
  getIn,
  isActive,
  isCheckable,
  isChecked,
  isPlainObject,
  normalizeSearch,
  setIn,
  unsetAndPrune,
} from './feature-tree-model';

const FILTER_OPTIONS = [
  { value: FEATURE_FILTER.ALL, label: 'Toutes' },
  { value: FEATURE_FILTER.ACTIVE, label: 'Activées' },
  { value: FEATURE_FILTER.INACTIVE, label: 'Désactivées' },
];

const Tag = ({ className, children }) => (
  <span className={`tw-rounded-[3px] tw-px-1 tw-font-medium ${className}`}>
    {children}
  </span>
);

const FeatureHelp = ({ entry, dependencyLabel }) => {
  if (
    !entry?.description &&
    !entry?.unused &&
    !entry?.warning &&
    !dependencyLabel
  )
    return null;

  return (
    <span className="tw-flex tw-flex-col tw-gap-1 tw-pl-6 tw-text-[11px] tw-leading-[15px] tw-text-[#76808c]">
      <span className="tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-1.5 tw-gap-y-0.5">
        {entry?.unused && (
          <Tag className="tw-bg-amber-50 tw-text-amber-800">Sans effet</Tag>
        )}
        {entry?.surfaces?.map((surface) => (
          <Tag
            key={surface}
            className="tw-bg-[rgba(118,128,140,0.1)] tw-text-[#4a5360]"
          >
            {FEATURE_SURFACE_LABELS[surface] || surface}
          </Tag>
        ))}
        {entry?.description && <span>{entry.description}</span>}
      </span>
      {dependencyLabel && (
        <span className="tw-text-amber-800">
          Sans effet tant que « {dependencyLabel} » n’est pas coché.
        </span>
      )}
      {entry?.warning && (
        <span className="tw-rounded-[3px] tw-bg-amber-50 tw-px-1.5 tw-py-0.5 tw-text-amber-800">
          {entry.warning}
        </span>
      )}
    </span>
  );
};

const Checkbox = ({ checked, onChange, label, help, isMain }) => (
  <div className="tw-flex tw-flex-col tw-gap-0.5 tw-py-1">
    <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="tw-size-4 tw-cursor-pointer tw-accent-main"
      />
      <span
        className={`tw-text-[13px] ${
          isMain
            ? 'tw-font-medium tw-text-text-dark-high-importance'
            : 'tw-text-black'
        }`}
      >
        {label}
      </span>
    </label>
    {help}
  </div>
);

const InlineField = ({ label, help, children }) => (
  <div className="tw-flex tw-flex-col tw-gap-0.5 tw-py-1">
    <label className="tw-flex tw-items-center tw-gap-3">
      <span className="tw-w-48 tw-shrink-0 tw-pl-6 tw-text-[13px] tw-text-black">
        {label}
      </span>
      {children}
    </label>
    {help}
  </div>
);

const inputClassName =
  'tw-h-8 tw-w-full tw-max-w-[420px] tw-rounded-[5px] tw-border tw-border-solid tw-border-input-border tw-bg-white tw-px-2.5 tw-text-[13px] focus:tw-border-input-borderFocused focus:tw-outline-none';

/**
 * Champ d'un réglage (texte, liste de choix, nombre, liste, JSON). Vider un
 * champ retire le réglage (`undefined`) : un nombre vidé ne devient pas `null`,
 * que l'API refuserait comme changement de type.
 */
const SettingInput = ({ node, update }) => {
  const { type, value, entry } = node;

  if (type === 'text' && entry?.options) {
    return (
      <select
        className={`${inputClassName} tw-max-w-[320px]`}
        value={value ?? ''}
        onChange={(event) => update(event.target.value || undefined)}
      >
        <option value="">Non renseigné</option>
        {entry.options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  if (type === 'text') {
    return (
      <input
        className={inputClassName}
        value={value ?? ''}
        placeholder="Non renseigné"
        onChange={(event) => update(event.target.value)}
      />
    );
  }

  if (type === 'number') {
    return (
      <input
        type="number"
        className={`${inputClassName} tw-max-w-[120px]`}
        value={value ?? ''}
        onChange={(event) =>
          update(
            event.target.value === '' ? undefined : Number(event.target.value)
          )
        }
      />
    );
  }

  if (type === 'list') {
    return (
      <input
        className={inputClassName}
        value={(value || []).join(', ')}
        placeholder="Valeurs séparées par des virgules"
        onChange={(event) =>
          update(
            event.target.value
              .split(',')
              .map((item) => item.trim())
              .filter(Boolean)
          )
        }
      />
    );
  }

  return (
    <span className="tw-text-[11px] tw-text-[#76808c]">
      {value === undefined
        ? 'Non renseigné · à saisir dans l’éditeur JSON'
        : `${Array.isArray(value) ? `${value.length} élément(s)` : 'Renseigné'} · modifiable dans l’éditeur JSON`}
    </span>
  );
};

/**
 * Rend une option et ses sous-options. Une option cochable (case) n'ouvre ses
 * sous-options qu'une fois cochée ; une sous-option restée active sous une
 * option décochée est montrée, signalée sans effet.
 */
const FeatureNode = ({ node, parent, onChange }) => {
  const catalog = useFeaturesCatalog();
  const label = getFeatureLabel(catalog, node.path);
  const dependencyLabel =
    parent && isCheckable(parent) && !isChecked(parent) && isActive(node)
      ? getFeatureLabel(catalog, parent.path)
      : null;
  const help = (
    <FeatureHelp entry={node.entry} dependencyLabel={dependencyLabel} />
  );

  const children = node.visibleChildren.length > 0 && (
    <div className="tw-ml-[7px] tw-border-0 tw-border-l tw-border-solid tw-border-[#eeeff2] tw-pl-4">
      {node.visibleChildren.map((child) => (
        <FeatureNode
          key={child.key}
          node={child}
          parent={node}
          onChange={onChange}
        />
      ))}
    </div>
  );

  if (isCheckable(node)) {
    return (
      <div>
        <Checkbox
          checked={isChecked(node)}
          onChange={(checked) =>
            onChange(
              node.type === 'toggle' ? [...node.path, 'enabled'] : node.path,
              checked
            )
          }
          label={label}
          help={help}
          isMain={node.type === 'toggle' || node.children.length > 0}
        />
        {children}
      </div>
    );
  }

  if (node.type === 'group') {
    return (
      <div>
        <div className="tw-flex tw-flex-col tw-gap-0.5 tw-pb-0.5 tw-pt-1.5">
          <span className="tw-text-[13px] tw-font-medium tw-text-text-dark-high-importance">
            {label}
          </span>
          {help}
        </div>
        {children}
      </div>
    );
  }

  return (
    <InlineField label={label} help={help}>
      <SettingInput
        node={node}
        update={(nextValue) => onChange(node.path, nextValue)}
      />
    </InlineField>
  );
};

const FeatureSection = ({
  title,
  description,
  checkedCount,
  isOpen,
  isForcedOpen,
  onToggle,
  children,
}) => {
  const isExpanded = isOpen || isForcedOpen;

  return (
    <div className="tw-border-0 tw-border-b tw-border-solid tw-border-[#eeeff2] last:tw-border-b-0">
      <button
        type="button"
        onClick={onToggle}
        disabled={isForcedOpen}
        className="tw-flex tw-w-full tw-items-center tw-gap-3 tw-border-0 tw-bg-white tw-px-4 tw-py-3 tw-text-left hover:tw-bg-[rgba(118,128,140,0.04)] disabled:tw-cursor-default"
      >
        <FontAwesomeIcon
          icon={isExpanded ? faChevronDown : faChevronRight}
          className="tw-size-3 tw-text-[#76808c]"
        />
        <span className="tw-flex tw-flex-1 tw-flex-col">
          <span className="tw-text-[13px] tw-font-semibold tw-text-text-dark-high-importance">
            {title}
          </span>
          {description && (
            <span className="tw-text-[11px] tw-text-[#76808c]">
              {description}
            </span>
          )}
        </span>
        <span className="tw-rounded-full tw-bg-[rgba(7,153,146,0.08)] tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-medium tw-text-main-active">
          {checkedCount} activée{checkedCount > 1 ? 's' : ''}
        </span>
      </button>
      {isExpanded && <div className="tw-px-4 tw-pb-3 tw-pl-10">{children}</div>}
    </div>
  );
};

const toolbarButtonClassName =
  'tw-h-7 tw-rounded-[5px] tw-border tw-border-solid tw-border-[#e6e8ec] tw-bg-white tw-px-2.5 tw-text-[12px] tw-text-text-dark-high-importance hover:tw-bg-[rgba(118,128,140,0.06)]';

const FeatureTreeToolbar = ({
  filter,
  onFilterChange,
  onExpandAll,
  onCollapseAll,
  isForcedOpen,
}) => (
  <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
    <div
      role="radiogroup"
      aria-label="Filtrer les options"
      className="tw-inline-flex tw-overflow-hidden tw-rounded-[5px] tw-border tw-border-solid tw-border-[#e6e8ec]"
    >
      {FILTER_OPTIONS.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={filter === option.value}
          onClick={() => onFilterChange(option.value)}
          className={`tw-h-7 tw-border-0 tw-px-3 tw-text-[12px] ${
            filter === option.value
              ? 'tw-bg-[rgba(7,153,146,0.08)] tw-font-medium tw-text-main-active'
              : 'tw-bg-white tw-text-[#4a5360] hover:tw-bg-[rgba(118,128,140,0.06)]'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
    <div className="tw-flex tw-gap-2">
      <button
        type="button"
        className={toolbarButtonClassName}
        onClick={onExpandAll}
        disabled={isForcedOpen}
      >
        Tout déplier
      </button>
      <button
        type="button"
        className={toolbarButtonClassName}
        onClick={onCollapseAll}
        disabled={isForcedOpen}
      >
        Tout replier
      </button>
    </div>
  </div>
);

/**
 * Options regroupées par section de premier niveau, construites à partir du
 * catalogue puis fusionnées avec la valeur du client (voir
 * feature-tree-model.js). Avec une recherche ou un filtre, seules les
 * sections et options correspondantes s'affichent, dépliées.
 *
 * `baseline` : configuration de référence (enregistrée, ou par défaut). Une
 * option absente de `baseline` qu'on décoche ou qu'on vide est retirée du JSON
 * plutôt qu'écrite à `false` ou vide.
 */
export const FeatureTree = ({ value, onChange, search = '', baseline }) => {
  const catalog = useFeaturesCatalog();
  const query = normalizeSearch(search);
  const [filter, setFilter] = useState(FEATURE_FILTER.ALL);
  const [openSections, setOpenSections] = useState([]);

  const sections = useMemo(
    () => buildFeatureTree(catalog, value || {}),
    [catalog, value]
  );

  const isFiltering = Boolean(query) || filter !== FEATURE_FILTER.ALL;
  const visibleSections = sections
    .map((section) => ({
      ...section,
      visibleNodes: filterFeatureNodes(catalog, section.nodes, {
        query,
        filter,
      }),
    }))
    .filter((section) => section.visibleNodes.length > 0);

  const handleChange = (path, nextValue) => {
    const isEmpty =
      nextValue === undefined ||
      nextValue === false ||
      nextValue === '' ||
      (Array.isArray(nextValue) && nextValue.length === 0);
    const isAbsentFromBaseline =
      baseline !== undefined && getIn(baseline, path) === undefined;

    onChange(
      nextValue === undefined || (isEmpty && isAbsentFromBaseline)
        ? unsetAndPrune(value, path, baseline)
        : setIn(value, path, nextValue)
    );
  };

  const toggleSection = (key) =>
    setOpenSections((previous) =>
      previous.includes(key)
        ? previous.filter((openKey) => openKey !== key)
        : [...previous, key]
    );

  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      <FeatureTreeToolbar
        filter={filter}
        onFilterChange={setFilter}
        onExpandAll={() =>
          setOpenSections(sections.map((section) => section.key))
        }
        onCollapseAll={() => setOpenSections([])}
        isForcedOpen={isFiltering}
      />

      {visibleSections.length === 0 ? (
        <p className="tw-m-0 tw-rounded-[6px] tw-border tw-border-dashed tw-border-[#d0d4da] tw-px-4 tw-py-6 tw-text-center tw-text-[13px] tw-text-[#76808c]">
          {query
            ? `Aucune option ne correspond à « ${search.trim()} ».`
            : 'Aucune option ne correspond à ce filtre.'}
        </p>
      ) : (
        <div className="tw-overflow-hidden tw-rounded-[6px] tw-border tw-border-solid tw-border-[#e6e8ec]">
          {visibleSections.map((section) => (
            <FeatureSection
              key={section.key}
              title={section.title}
              description={section.description}
              checkedCount={countChecked(section.nodes)}
              isOpen={openSections.includes(section.key)}
              isForcedOpen={isFiltering}
              onToggle={() => toggleSection(section.key)}
            >
              {section.visibleNodes.map((node) => (
                <FeatureNode
                  key={node.key}
                  node={node}
                  onChange={handleChange}
                />
              ))}
            </FeatureSection>
          ))}
        </div>
      )}
    </div>
  );
};

export const JsonEditor = ({ value, onChange }) => {
  const [text, setText] = useState(() => JSON.stringify(value, null, 2));
  const [error, setError] = useState(null);
  const [emittedValue, setEmittedValue] = useState(value);

  // Resynchronise le texte quand la valeur change hors de l'éditeur (copie
  // d'un client, valeurs par défaut). Une valeur émise par la saisie elle-même
  // est ignorée, sinon le texte serait reformaté à chaque frappe.
  if (value !== emittedValue) {
    setEmittedValue(value);
    setText(JSON.stringify(value, null, 2));
    setError(null);
  }

  const handleChange = (nextText) => {
    setText(nextText);

    try {
      const parsed = JSON.parse(nextText);

      if (!isPlainObject(parsed)) {
        setError('La configuration doit être un objet JSON.');
        return;
      }

      setError(null);
      setEmittedValue(parsed);
      onChange(parsed);
    } catch (parseError) {
      setError(`JSON invalide : ${parseError.message}`);
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <textarea
        value={text}
        onChange={(event) => handleChange(event.target.value)}
        spellCheck={false}
        className={`tw-h-[420px] tw-w-full tw-resize-y tw-rounded-[5px] tw-border tw-border-solid tw-bg-white tw-p-3 tw-font-mono tw-text-[12px] focus:tw-outline-none ${
          error ? 'tw-border-input-required' : 'tw-border-input-border'
        }`}
      />
      {error && (
        <div className="tw-text-[11px] tw-text-input-required">{error}</div>
      )}
    </div>
  );
};
